// src/pages/partner_management/PartnerFilterBar.tsx
//
// Filter bar for the Partner Overview (EE-272), modelled on the Requisition
// and Inquiry bars. Every control maps onto one named query param that
// PartnerController.get_partners accepts; the two dropdowns are code-keyed
// (render the label, filter by the code) against the same value lists the
// partner form writes, so a filter can only ever ask for a value a row can
// hold.
//
// The controls come from components/Dashboard/filterFields, shared with the
// other two bars.
import { FunctionComponent, useRef } from "react";
import { InquiryTypeEnum } from "types/InquiryTypes";
import { DELIVERY_METHODS } from "pages/outsourcing_management/inquiryFormValues";
import {
  asOptions,
  ClearFiltersButton,
  FilterDateField,
  FilterRow,
  FilterSelect,
} from "components/Dashboard/filterFields";

export type PartnerFilterState = {
  templateType: string;
  templateDelivery: string;
  createdFrom: string;
  createdTo: string;
};

export const EMPTY_PARTNER_FILTERS: PartnerFilterState = {
  templateType: "",
  templateDelivery: "",
  createdFrom: "",
  createdTo: "",
};

// Matches CreateOrUpdatePartnerModal's dropdowns - PARTNERS.template_type and
// PARTNERS.template_delivery store these strings verbatim.
const SERVICE_TYPES = asOptions(Object.values(InquiryTypeEnum));
const DELIVERY_OPTIONS = asOptions(DELIVERY_METHODS);

type PropsType = {
  filters: PartnerFilterState;
  onChange: (filters: PartnerFilterState) => void;
};

const PartnerFilterBar: FunctionComponent<PropsType> = ({
  filters,
  onChange,
}) => {
  // See InquiryFilterBar: patch onto the latest filters, not the captured
  // prop, so two controls changed in quick succession both land.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const patch = (changes: Partial<PartnerFilterState>) =>
    onChange({ ...filtersRef.current, ...changes });

  const hasActiveFilters = Object.values(filters).some((value) => !!value);

  return (
    <FilterRow>
      <FilterSelect
        label="Service type"
        value={filters.templateType}
        onChange={(templateType) => patch({ templateType })}
        options={SERVICE_TYPES}
      />
      <FilterSelect
        label="Delivery"
        value={filters.templateDelivery}
        onChange={(templateDelivery) => patch({ templateDelivery })}
        options={DELIVERY_OPTIONS}
      />
      <FilterDateField
        label="Created From"
        value={filters.createdFrom}
        onChange={(createdFrom) => patch({ createdFrom })}
      />
      <FilterDateField
        label="Created To"
        value={filters.createdTo}
        onChange={(createdTo) => patch({ createdTo })}
      />
      <ClearFiltersButton
        disabled={!hasActiveFilters}
        onClick={() => onChange({ ...EMPTY_PARTNER_FILTERS })}
      />
    </FilterRow>
  );
};

export default PartnerFilterBar;
